/**
 * Admin — Audit layout route (3 sub-tabs).
 *
 * Sub-tabs:
 *   /admin/audit                — Timeline (default) — every admin action chronologically
 *   /admin/audit/changes        — Change sets — adjacent same-actor events grouped
 *   /admin/audit/integrity      — Hash chain verification + external witness
 *
 * Note: this is the ADMIN-scoped audit (config + security + platform changes).
 * Tenant-scoped audit (user content, AI queries) lives under Trust → Activity.
 */

import { createFileRoute, Outlet, Link, useLocation } from '@tanstack/react-router';
import {
  ScrollText, GitCommit, ShieldCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/audit')({
  component: AuditLayout,
});

const SUB_TABS = [
  { label: 'Timeline',     path: '/admin/audit',           icon: ScrollText },
  { label: 'Change Sets',  path: '/admin/audit/changes',   icon: GitCommit },
  { label: 'Integrity',    path: '/admin/audit/integrity', icon: ShieldCheck },
];

function AuditLayout() {
  const location = useLocation();

  return (
    <div className="flex flex-col h-full">
      <div className="border-b bg-background px-6 shrink-0">
        <nav className="flex items-center gap-1 overflow-x-auto whitespace-nowrap">
          {SUB_TABS.map(t => {
            const Icon = t.icon;
            const isActive = location.pathname === t.path
              || (t.path !== '/admin/audit' && location.pathname.startsWith(t.path));
            const isExact = location.pathname === '/admin/audit' || location.pathname === '/admin/audit/';
            const active = t.path === '/admin/audit' ? isExact : isActive;
            return (
              <Link
                key={t.path}
                to={t.path as any}
                title={t.label}
                aria-label={t.label}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors -mb-px shrink-0 whitespace-nowrap',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden md:inline">{t.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
